import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, FileUp, Keyboard, Loader2, ScanLine, ArrowLeft, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { API } from '@/utils/api';
import { Field, VehicleFields, EMPTY_VEHICLE } from './VehicleFields';
import { DuplicatesPanel } from './DuplicatesPanel';
import { QrScan } from './QrScan';

export const EMPTY_CLIENT = { client_type: 'particulier', company_name: '', first_name: '', last_name: '', phone: '', whatsapp: '', email: '', address: '', postal_code: '', city: '', birthday: '' };
const OWNER_MAP = { owner_company: 'company_name', owner_last_name: 'last_name', owner_first_name: 'first_name', owner_address: 'address', owner_postal_code: 'postal_code', owner_locality: 'city', owner_city: 'city' };

const fromScan = (raw) => {
  const d = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, v ?? '']));
  const npa = d.owner_postal_code || (d.owner_city.match(/\b\d{4}\b/) || [''])[0];
  const city = d.owner_locality || d.owner_city.replace(/\b\d{4}\b/, '').trim();
  const client = { ...EMPTY_CLIENT, client_type: d.owner_company ? 'entreprise' : 'particulier', company_name: d.owner_company, first_name: d.owner_first_name, last_name: d.owner_last_name || (d.owner_company ? d.owner_company : ''), address: d.owner_address.replace(npa, '').replace(city, '').replace(/[,\s]+$/, ''), postal_code: npa, city, birthday: d.owner_dob };
  const vehicle = Object.fromEntries(Object.keys(EMPTY_VEHICLE).map(k => [k, d[k] ?? EMPTY_VEHICLE[k]]));
  const unc = d.uncertain_fields || [];
  return { client, vehicle, uncertain: [...new Set(unc.map(f => OWNER_MAP[f] || f))] };
};

export const ScanChoice = ({ onScanned, onManual }) => {
  const [busy, setBusy] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const cam = useRef(null); const imp = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f);
    try { const r = await axios.post(`${API}/vehicles/scan-carte-grise`, fd); onScanned(fromScan(r.data)); toast.success('Permis lu, vérifiez les informations'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Lecture impossible, essayez une photo plus nette'); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-2" data-testid="new-client-choice">
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="scan-camera-input" />
      <input ref={imp} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="scan-import-input" />
      <div className="rounded-xl border-2 border-slate-900 p-3 space-y-3">
        <p className="font-semibold flex items-center gap-2"><ScanLine className="w-4 h-4" />Scanner le permis de circulation suisse</p>
        {busy ? <p className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Lecture en cours…</p>
          : showQr ? <QrScan onResult={(r) => { toast.success('Carte grise lue, vérifiez les informations'); onScanned(fromScan(r)); }} />
            : <Button type="button" className="w-full h-11 bg-[#0E1116] text-[#FACC15] hover:bg-black" onClick={() => setShowQr(true)} data-testid="scan-qr-btn"><QrCode className="w-5 h-5 mr-2" />Scanner avec le téléphone (QR code)</Button>}
        {!busy && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => imp.current.click()} data-testid="scan-import-btn"><FileUp className="w-4 h-4 mr-1" />Photo ou PDF depuis l'ordinateur</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => cam.current.click()} data-testid="scan-camera-btn"><Camera className="w-4 h-4 mr-1" />Appareil photo de cet appareil</Button>
          </div>
        )}
      </div>
      {onManual && <Button type="button" variant="outline" onClick={onManual} data-testid="manual-entry-btn"><Keyboard className="w-4 h-4 mr-2" />Saisie manuelle / permis indisponible</Button>}
    </div>
  );
};

const ClientFields = ({ c, set, uncertain }) => (
  <div className="grid grid-cols-2 gap-2">
    <div className="col-span-2 flex gap-1">
      {['particulier', 'entreprise'].map(t => (
        <button key={t} type="button" onClick={() => set({ ...c, client_type: t })} data-testid={`client-type-${t}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${c.client_type === t ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300'}`}>{t === 'particulier' ? 'Particulier' : 'Entreprise'}</button>
      ))}
    </div>
    {c.client_type === 'entreprise' && <div className="col-span-2"><Field label="Raison sociale" value={c.company_name} uncertain={uncertain.includes('company_name')} onChange={v => set({ ...c, company_name: v })} testid="client-company-input" /></div>}
    <Field label="Prénom" value={c.first_name} uncertain={uncertain.includes('first_name')} onChange={v => set({ ...c, first_name: v })} testid="client-first-name-input" />
    <Field label="Nom" value={c.last_name} uncertain={uncertain.includes('last_name')} onChange={v => set({ ...c, last_name: v })} testid="client-last-name-input" />
    <div className="col-span-2"><Field label="Adresse (peut être complétée plus tard)" value={c.address} uncertain={uncertain.includes('address')} onChange={v => set({ ...c, address: v })} testid="client-address-input" /></div>
    <Field label="NPA" value={c.postal_code} uncertain={uncertain.includes('postal_code')} onChange={v => set({ ...c, postal_code: v })} testid="client-npa-input" />
    <Field label="Localité" value={c.city} uncertain={uncertain.includes('city')} onChange={v => set({ ...c, city: v })} testid="client-city-input" />
  </div>
);

const ContactFields = ({ c, set, sameWa, setSameWa }) => (
  <div className="grid grid-cols-2 gap-2">
    <Field label="Téléphone" value={c.phone} onChange={v => set({ ...c, phone: v, whatsapp: sameWa ? v : c.whatsapp })} testid="client-phone-input" type="tel" />
    <Field label="E-mail" value={c.email} onChange={v => set({ ...c, email: v })} testid="client-email-input" type="email" />
    <div className="col-span-2 flex items-center gap-2 text-sm">
      <Checkbox checked={sameWa} onCheckedChange={(v) => { setSameWa(!!v); if (v) set({ ...c, whatsapp: c.phone }); }} data-testid="whatsapp-same-checkbox" />WhatsApp identique au téléphone
    </div>
    {!sameWa && <div className="col-span-2"><Field label="Numéro WhatsApp" value={c.whatsapp} onChange={v => set({ ...c, whatsapp: v })} testid="client-whatsapp-input" type="tel" /></div>}
  </div>
);

const fromQuery = (q = '') => {
  if (q.replace(/\D/g, '').length >= 6 && /^[\d\s+().-]+$/.test(q)) return { c: { ...EMPTY_CLIENT, phone: q, whatsapp: q }, veh: null };
  if (/^[A-Za-z]{2}\s?\d{1,6}$/.test(q)) return { c: EMPTY_CLIENT, veh: { ...EMPTY_VEHICLE, plate: q.toUpperCase() } };
  const parts = q.split(/\s+/).filter(Boolean);
  return { c: { ...EMPTY_CLIENT, first_name: parts.length > 1 ? parts[0] : '', last_name: parts.length > 1 ? parts.slice(1).join(' ') : (parts[0] || '') }, veh: null };
};

export const NewClientSection = ({ onDone, onUseExisting, onCancel, initialQuery = '', initialMode = 'choice' }) => {
  const [mode, setMode] = useState(initialMode);
  const [c, setC] = useState(() => fromQuery(initialQuery).c);
  const [veh, setVeh] = useState(() => fromQuery(initialQuery).veh);
  const [scanned, setScanned] = useState(null);
  const [uncertain, setUncertain] = useState([]);
  const [isHolder, setIsHolder] = useState(true);
  const [sameWa, setSameWa] = useState(true);
  const [dups, setDups] = useState(null);

  const onScanned = ({ client, vehicle, uncertain: u }) => { setScanned(client); setC(client); setVeh(vehicle); setUncertain(u); setMode('form'); };
  const toggleHolder = (v) => { setIsHolder(v); setC(v && scanned ? { ...scanned, phone: c.phone, email: c.email, whatsapp: c.whatsapp } : { ...EMPTY_CLIENT, phone: c.phone, email: c.email, whatsapp: c.whatsapp }); };

  const next = async () => {
    if (!(c.last_name.trim() || c.company_name.trim())) { toast.error('Il manque le nom du client (ou la raison sociale pour une entreprise)'); return; }
    if (!(c.phone.trim() || c.email.trim())) { toast.error('Il manque un moyen de contact : téléphone ou e-mail (un des deux suffit)'); return; }
    let r;
    try { r = await axios.post(`${API}/agenda/check-duplicates`, { ...c, plate: veh?.plate || '', vin: veh?.chassis_number || '' }); }
    catch { toast.error('Vérification des doublons impossible, réessayez'); return; }
    if (r.data.clients.length || r.data.vehicles.length) { setDups(r.data); return; }
    onDone({ client: c, vehicle: veh, ignoreDuplicates: false });
  };

  if (dups) return <DuplicatesPanel dups={dups} onUse={onUseExisting} onIgnore={() => onDone({ client: c, vehicle: veh, ignoreDuplicates: true })} onBack={() => setDups(null)} />;
  if (mode === 'choice') return (
    <section className="space-y-2" data-testid="new-client-section">
      <button type="button" onClick={onCancel} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-900" data-testid="new-client-cancel"><ArrowLeft className="w-3 h-3" />Retour à la recherche</button>
      <ScanChoice onScanned={onScanned} onManual={() => setMode('form')} />
    </section>
  );
  return (
    <section className="space-y-4" data-testid="new-client-form">
      <button type="button" onClick={() => setMode('choice')} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-900"><ArrowLeft className="w-3 h-3" />Changer de méthode</button>
      {scanned && (
        <label className="flex items-start gap-2 text-sm p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="holder-confirm">
          <Checkbox checked={isHolder} onCheckedChange={(v) => toggleHolder(!!v)} data-testid="holder-confirm-checkbox" />
          <span>Le détenteur du permis est bien le client à enregistrer. <span className="text-slate-500">Si vous décochez, saisissez le client ; le véhicule scanné lui sera rattaché.</span></span>
        </label>
      )}
      {uncertain.length > 0 && <p className="text-xs text-amber-700" data-testid="uncertain-note">Les champs en orange sont incertains : vérifiez-les sur le document.</p>}
      <ClientFields c={c} set={setC} uncertain={isHolder ? uncertain : []} />
      <div><p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Contact</p><ContactFields c={c} set={setC} sameWa={sameWa} setSameWa={setSameWa} /></div>
      <div>
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Véhicule {!veh && '(facultatif)'}</p>
        {veh ? <VehicleFields value={veh} onChange={setVeh} uncertain={uncertain} compact={!scanned} />
          : <Button type="button" size="sm" variant="outline" onClick={() => setVeh({ ...EMPTY_VEHICLE })} data-testid="new-client-add-vehicle-btn">Ajouter le véhicule maintenant</Button>}
        {veh && !scanned && <button type="button" className="text-xs text-slate-500 mt-1 underline" onClick={() => setVeh(null)}>Plus tard, à l'arrivée</button>}
      </div>
      <Button type="button" className="w-full" onClick={next} data-testid="new-client-continue-btn">Continuer</Button>
    </section>
  );
};
